/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1943732227 — Логування подій (event_log) */

import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';

import { IS_PROD_DB } from '../../../shared/env.config';
import { useAppStore } from '../../../shared/hooks/useAppStore';
import { useTimerStore } from '../../../shared/hooks/useTimerStore';
import { debug } from '../../../shared/lib/general';
import { createEventLog } from '../api/event-log.api';
import {
    CreateEventLogRequestDto,
    EventLogType,
} from '../api/event-log.api-types';
import { EventName, TimeoutName } from '../event-log.constants';

interface SendArgs<TName extends string> {
    /** Label describing the operation / screen — written to `timeout_name`. */
    name: TName;
    /** Override the auto-attached current timer value (seconds remaining). */
    timeout_value?: number;
}

export const useSendEventLog = () => {
    const { mutate } = useMutation({
        mutationFn: createEventLog,
        onError: (error) => {
            debug('event-log send failed', 'error', error);
        },
    });

    // Read order + timer imperatively via getState() — subscribing to the timer
    // would re-render every consumer (Google Pay button, etc.) every second.
    const post = useCallback(
        (type: EventLogType, { name, timeout_value }: SendArgs<string>) => {
            // Should send logs when using production DB
            if (!IS_PROD_DB) {
                return;
            }
            const orderId = Number(useAppStore.getState().order?.id);
            if (!Number.isInteger(orderId) || orderId <= 0) {
                debug('event-log skipped: no order id', 'warn');
                return;
            }
            const dto: CreateEventLogRequestDto = {
                order_id: orderId,
                type,
                page_url: window.location.pathname,
                timeout_name: name,
                timeout_value: timeout_value ?? useTimerStore.getState().time,
            };
            mutate(dto);
        },
        [mutate],
    );

    const sendEvent = useCallback(
        (args: SendArgs<EventName>) => post('event', args),
        [post],
    );

    const sendTimeout = useCallback(
        (args: SendArgs<TimeoutName>) => post('timeout', args),
        [post],
    );

    return { sendEvent, sendTimeout };
};
